import DeleteIcon from "@mui/icons-material/DeleteOutline";
import DomainAddIcon from "@mui/icons-material/DomainAdd";
import EditIcon from "@mui/icons-material/Edit";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import ExitIcon from "@mui/icons-material/TransitEnterexit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { Theme } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";
import { styled } from "@mui/material/styles";
import Switch from "@mui/material/Switch";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { NotAuthorized } from "_shared/_errors/_views/not-authorized";
import { ConfirmResponse, useConfirmDialog } from "_shared/_providers/dialogs/confirm/confirm-dialog";
import { useDialog } from "_shared/_providers/dialogs/dialog";
import { RootState, useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import Guid from "_shared/guids/guid";
import * as Config from "config";
import CompanyAvatar from "marketplace/_views/avatars/company-avatar";
import IndividualAvatar from "marketplace/_views/avatars/individual-avatar";
import TeamAvatar from "marketplace/_views/avatars/team-avatar";
import { PageTab } from "marketplace/_views/profiles/profiles-page-tabs";
import {
  getCompanyVendors,
  getCompanyVendorsError,
  getCompanyVendorsIsLoading,
  populateCompanyVendors,
  updateCompany,
} from "marketplace/companies/_store/companies-redux-slice";
import Company from "marketplace/companies/company";
import CompanyProfile from "marketplace/companies/profiles/company-profile";
import {
  getIndividualVendors,
  getIndividualVendorsError,
  getIndividualVendorsIsLoading,
  populateIndividualVendors,
  updateIndividual,
} from "marketplace/individuals/_stores/individuals-redux-slice";
import Individual from "marketplace/individuals/individual";
import IndividualProfile from "marketplace/individuals/profiles/individual-profile";
import TeamProfile from "marketplace/team-profile/team-profile";
import {
  getTeamVendors,
  getTeamVendorsError,
  getTeamVendorsIsLoading,
} from "marketplace/teams/_store/marketplace-teams-redux-slice";
import {
  useDeleteTeamMutation,
  useGetTeamVendorsQuery,
  useRemoveTeamMemberMutation,
  useUpdateTeamMutation,
} from "marketplace/teams/_api/marketplace-team-api";
import Team from "marketplace/teams/team";
import {
  createMRTColumnHelper,
  type MRT_TableOptions,
  MRT_ShowHideColumnsButton as ShowHideColumnsButton,
  MRT_ToggleDensePaddingButton as ToggleDensePaddingButton,
  MRT_ToggleFiltersButton as ToggleFiltersButton,
  MRT_ToggleFullScreenButton as ToggleFullScreenButton,
  MRT_ToggleGlobalFilterButton as ToggleGlobalFilterButton,
  type MRT_ColumnFiltersState as ColumnFiltersState,
  type MRT_PaginationState as PaginationState,
  type MRT_SortingState as SortingState,
} from "material-react-table";
import { enqueueSnackbar } from "notistack";
import { getSessionPermissions, populateSessionPermissions } from "permissions/_stores/permissions-redux-slice";
import React, { JSX, useCallback, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useLoaderData, useNavigate } from "react-router";
import { useSearchParams } from "react-router-dom";
import { useSession } from "users/__session/session-context";
import { AccountType } from "users/account-types/account-type";
import { editComponentMap, viewComponentMap } from "./types";
import Loader from "_shared/_views/loading/loader";
import { MRTNoMemo } from "_shared/_views/tables/table";

const ToolbarButtonsContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
  },
  display: "flex",
  alignItems: "end",
  flexDirection: "column",
  gap: theme.spacing(1),
}));
const ToolbarButtons = styled("div")(() => ({
  display: "flex",
  alignItems: "end",
  flexWrap: "nowrap",
}));
const TableTopToolbarContainer = styled("div")(() => ({
  display: "flex",
  flexDirection: "column",
  width: "100%",
}));
const LowerTopToolbarContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down("md")]: {
    flexDirection: "column",
  },
  display: "flex",
  flexDirection: "row",
  alignItems: "left",
  width: "100%",
}));
const TableTitle = styled(Typography)(({ theme }) => ({
  paddingLeft: theme.spacing(1),
}));
const NewProfileButtonContainer = styled("span")<{ disabled: boolean }>(({ disabled }) => ({
  cursor: disabled ? "not-allowed" : "default",
}));
const newProfileIcon = (activeTab: PageTab) => {
  if (activeTab === PageTab.Individual) return <PersonAddIcon fontSize="large" />;
  if (activeTab === PageTab.Company) return <DomainAddIcon fontSize="large" />;
  if (activeTab === PageTab.Teams) return <GroupAddIcon fontSize="large" />;
};
const NameCellContainer = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
}));
const CategoryChip = styled(Chip)(({ theme }) => ({
  color: "#777",
  marginRight: theme.spacing(0.4),
}));
const RowTitle = styled(Typography)(({ theme }) => ({
  fontSize: "1.1em",
  fontWeight: "600",
  marginLeft: theme.spacing(1.5),
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));
const NewButton = styled(IconButton)(() => ({
  padding: 0,
}));
const LoaderContainer = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  height: "100%",
}));

type ProfileTableData = Individual | Company | Team;

// TODO: Split page by Individual | Company | Team

const columnHelper = createMRTColumnHelper<ProfileTableData>();

export default function ManageProfiles() {
  // Refs
  const isDirtyRef = React.useRef<boolean>(false);

  // State
  // Remove local state for activeTab, derive from routeParams instead
  // const [activeTab, setActiveTab] = React.useState<PageTab>();
  const [query, setQuery] = React.useState("");
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [pagination, setPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  // Hooks
  const routeParams = useLoaderData();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const confirm = useConfirmDialog();
  const { openDialog, closeDialog, numDialogs } = useDialog();
  const dispatch = useAttorneyHubDispatch();
  const session = useSession();
  const isMediumDisplaySize = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));

  const [updateTeamMutation] = useUpdateTeamMutation();
  const [deleteTeamMutation] = useDeleteTeamMutation();
  const [removeTeamMemberMutation] = useRemoveTeamMemberMutation();

  const activeTab = routeParams.pageTab as PageTab | undefined;

  // Session Permissions
  const sessionPermissions = useSelector(getSessionPermissions);
  const isLoadingSessionPermissions = useSelector((state: RootState) => state.permissions.session.loading);
  const wasErrorLoadingSessionPermissions = useSelector((state: RootState) => state.permissions.session.error);
  useEffect(() => {
    if (!sessionPermissions && !isLoadingSessionPermissions && !wasErrorLoadingSessionPermissions) {
      void dispatch(populateSessionPermissions({ session }));
    }
  }, [sessionPermissions, isLoadingSessionPermissions, wasErrorLoadingSessionPermissions, dispatch, session]);

  // Individual Profiles
  const individualProfiles = useSelector(getIndividualVendors());
  const individualQueryIsLoading = useSelector(getIndividualVendorsIsLoading());
  const errorLoadingIndividualQuery = useSelector(getIndividualVendorsError());

  // Company Profiles
  const companyProfiles = useSelector(getCompanyVendors());
  const companyQueryIsLoading = useSelector(getCompanyVendorsIsLoading());
  const errorLoadingCompanyQuery = useSelector(getCompanyVendorsError());

  // Team Profiles
  const teamProfiles = useSelector(getTeamVendors());
  const teamQueryIsLoading = useSelector(getTeamVendorsIsLoading());
  const errorLoadingTeamQuery = useSelector(getTeamVendorsError());
  useGetTeamVendorsQuery(undefined, { skip: activeTab !== PageTab.Teams });

  // Populate profile data based on active tab
  useEffect(() => {
    if (activeTab === PageTab.Individual) {
      if (individualQueryIsLoading) return;
      if (individualProfiles !== undefined) return;
      dispatch(populateIndividualVendors());
    }
    if (activeTab === PageTab.Company) {
      if (companyQueryIsLoading) return;
      if (companyProfiles !== undefined) return;
      dispatch(populateCompanyVendors());
    }
  }, [
    dispatch,
    individualQueryIsLoading,
    individualProfiles,
    companyQueryIsLoading,
    companyProfiles,
    activeTab,
  ]);

  const createProfile = useCallback(() => {
    if (!activeTab) return;

    const ProfileComponent = editComponentMap[activeTab];
    const parentURLString = `/marketplace/profiles/${activeTab}`;

    openDialog({
      titleStyle: {
        paddingBottom: 0,
      },
      MuiProps: {
        maxWidth: activeTab === PageTab.Individual ? "lg" : "md",
        fullWidth: true,
        style: {
          overflowX: "hidden",
          paddingBottom: 0,
        },
      },
      onClose: () => {
        navigate(parentURLString);
      },
      component: (
        <ProfileComponent
          navigate={navigate}
          searchParams={searchParams}
          setSearchParams={setSearchParams}
          isVisible={false}
          onSave={(profile) => {
            if (!(profile instanceof Team)) return;
            closeDialog();
            navigate(`${parentURLString}/edit/${profile.id}`);
          }}
          onFormDirty={(isDirty?: boolean) => {
            isDirtyRef.current = isDirty ?? true;
          }}
        />
      ),
    });
  }, [activeTab, closeDialog, navigate, openDialog, searchParams, setSearchParams]);

  const openEditProfileDialog = useCallback(
    (profileType: PageTab, profileId: Guid, profileData?: IndividualProfile & CompanyProfile & TeamProfile) => {
      const ProfileComponent = editComponentMap[profileType];
      const parentURLString = profileType ? `/marketplace/profiles/${profileType}` : "/marketplace/profiles";

      openDialog({
        titleStyle: {
          paddingBottom: 0,
        },
        MuiProps: {
          maxWidth: profileType === PageTab.Individual ? "lg" : "md",
          fullWidth: true,
          style: {
            overflowX: "hidden",
            paddingBottom: 0,
          },
        },
        onClose: () => {
          navigate(parentURLString);
        },
        component: (
          <ProfileComponent
            navigate={navigate}
            searchParams={searchParams}
            setSearchParams={setSearchParams}
            individualId={profileType === PageTab.Individual ? profileId : undefined}
            companyId={profileType === PageTab.Company ? profileId : undefined}
            teamId={profileType === PageTab.Teams ? profileId : undefined}
            profile={profileData}
            onFormDirty={(isDirty?: boolean) => {
              isDirtyRef.current = isDirty ?? true;
            }}
          />
        ),
      });
    },
    [navigate, openDialog, searchParams, setSearchParams],
  );

  const openViewProfileDialog = useCallback(
    (profileType: PageTab, profileId: Guid, profileData?: IndividualProfile & CompanyProfile & TeamProfile) => {
      if (!profileId) {
        enqueueSnackbar(`${profileType} profile unavailable.`, { variant: "error" });
        return;
      }

      const ProfileComponent = viewComponentMap[profileType];
      const parentURLString = `/marketplace/profiles/${activeTab}`;

      openDialog({
        titleStyle: {
          paddingBottom: 0,
        },
        MuiProps: {
          maxWidth: profileType === PageTab.Individual ? "lg" : "md",
          fullWidth: true,
          style: {
            overflowX: "hidden",
            paddingBottom: 0,
          },
        },
        onClose: () => {
          navigate(parentURLString);
        },
        component: (
          <ProfileComponent individualId={profileId} companyId={profileId} teamId={profileId} profile={profileData} />
        ),
      });
    },
    [activeTab, navigate, openDialog],
  );

  useEffect(() => {
    if (!session.user?.legalEntityId) return;

    if (!routeParams.pageTab) {
      navigate(`/marketplace/profiles/${PageTab.Individual}`);
      return;
    }

    if (numDialogs > 0) return;
    if (routeParams.action === "create") {
      createProfile();
    }

    if (routeParams.id) {
      if (routeParams.action === "edit") {
        openEditProfileDialog(routeParams.pageTab as PageTab, new Guid(routeParams.id));
      }
      if (routeParams.action === "view") {
        openViewProfileDialog(routeParams.pageTab as PageTab, new Guid(routeParams.id));
      }
    }
  }, [
    routeParams,
    session.user?.legalEntityId,
    numDialogs,
    navigate,
    createProfile,
    openEditProfileDialog,
    openViewProfileDialog,
  ]);

  const readonlyTab: boolean = activeTab === PageTab.Company && !sessionPermissions?.includesAccessToAdministration;

  const getShouldDisableRowEditing = useCallback(
    (profile: ProfileTableData): boolean =>
      readonlyTab ||
      (activeTab === PageTab.Teams && profile instanceof Team && !profile?.leader?.userId?.isEqualTo(session.user?.id)),
    [readonlyTab, activeTab, session.user?.id],
  );

  const getShouldDisableAddButton = useCallback((): boolean => {
    if (readonlyTab || !sessionPermissions?.includesReadAccessToMarketplace) return true;
    if (activeTab === PageTab.Individual && individualProfiles !== undefined && individualProfiles.length > 0)
      return true;
    if (activeTab === PageTab.Company && companyProfiles !== undefined && companyProfiles.length > 0) return true;
    return activeTab === PageTab.Teams && session.accountType === AccountType.Client;
  }, [readonlyTab, sessionPermissions, activeTab, individualProfiles, companyProfiles, session.accountType]);

  const addProfileDisabledMessage = useCallback(() => {
    if (readonlyTab) return "You don't have permission";
    if (activeTab === PageTab.Individual && individualProfiles !== undefined && individualProfiles.length > 0)
      return "You can only have one individual profile";
    if (activeTab === PageTab.Company && companyProfiles !== undefined && companyProfiles.length > 0)
      return "You can only have one company profile";
    if (activeTab === PageTab.Teams && individualProfiles !== undefined && individualProfiles.length < 1)
      return "You must create an individual profile before creating a vendor-team";
    return "";
  }, [readonlyTab, activeTab, individualProfiles, companyProfiles]);

  const editProfile = useCallback(
    (profile: ProfileTableData) => {
      const editingDisabled = getShouldDisableRowEditing(profile);

      if (editingDisabled) {
        enqueueSnackbar("You don't have permission to edit this profile. Opening as read-only.", {
          variant: "warning",
        });
      }

      navigate(`/marketplace/profiles/${activeTab}/${editingDisabled ? "view" : "edit"}/${profile.id}`);
    },
    [activeTab, getShouldDisableRowEditing, navigate],
  );

  const publishProfile = useCallback(
    async (profile: ProfileTableData) => {
      if (!profile.profile) return;
      const name =
        profile instanceof Individual
          ? `${profile.profile.firstName} ${profile.profile.lastName}`
          : profile.profile.name;

      const response = await confirm({
        title: `${profile.isVisible ? "Hide" : "Show"} profile ${profile.isVisible ? "from" : "on"} the Marketplace?`,
        message: profile.isVisible
          ? `Hide ${name}'s profile? This profile will no longer be searchable on the Marketplace.`
          : `Show ${name}'s profile? This profile will appear in searches on the Marketplace.`,
      });

      if (response === ConfirmResponse.Cancel) return;

      try {
        profile.toggleVisibility();
        if (profile instanceof Company) {
          await dispatch(
            updateCompany({
              companyId: profile.id,
              isVisible: profile.isVisible,
            }),
          );
        } else if (profile instanceof Individual) {
          await dispatch(
            updateIndividual({
              individualId: profile.id,
              isVisible: profile.isVisible,
            }),
          );
        } else if (profile instanceof Team) {
          await updateTeamMutation({
            teamId: profile.id.value,
            originalTeam: { ...profile, ...{ isVisible: !profile.isVisible } } as Team,
            updatedTeam: profile,
          }).unwrap();
        }

        enqueueSnackbar(
          !profile.isVisible
            ? `${name}'s Profile now hidden from the Marketplace`
            : `${name}'s Profile now shown on the Marketplace`,
          { variant: "info" },
        );
      } catch (error) {
        console.error(error);
        enqueueSnackbar(
          !profile.isVisible
            ? "Couldn't hide profile from marketplace. Please try again"
            : "Couldn't show profile on marketplace. Please try again",
          { variant: "error" },
        );
      }
    },
    [confirm, dispatch, updateTeamMutation],
  );

  const deleteProfile = useCallback(
    async (profileData: ProfileTableData) => {
      if (!profileData.profile || !profileData.id) {
        throw new Error("Profile data is missing");
      }
      const name =
        profileData instanceof Individual
          ? `${profileData.profile.firstName} ${profileData.profile.lastName}`
          : profileData.profile.name;

      const response = await confirm({
        title: "Delete profile?",
        message: `Delete ${name} profile?`,
        okButtonText: "Delete",
      });

      if (response === ConfirmResponse.Cancel) return;

      try {
        await deleteTeamMutation({ teamId: profileData.id.value }).unwrap();
      } catch (error) {
        console.error(error);
        enqueueSnackbar("Couldn't delete profile. Please try again", { variant: "error" });
      }
    },
    [confirm, deleteTeamMutation],
  );

  const leaveTeam = useCallback(
    async (profile: ProfileTableData) => {
      if (!profile.profile) return;

      const team = profile as Team;
      if (!team.id || !team?.members) return;

      // Search vendor-team members for current user
      const membership = team.members.find((m) => m.userId?.isEqualTo(session.user?.id));
      if (!membership?.userId) return;

      const response = await confirm({
        title: "Leave vendor-team?",
        message: `Leave ${team.profile.name} team?`,
        okButtonText: "Leave",
      });

      if (response === ConfirmResponse.Cancel) return;

      await removeTeamMemberMutation({
        teamId: team.id.value,
        memberIndividualId: membership.userId.value,
      }).unwrap();
    },
    [session, confirm, removeTeamMemberMutation],
  );

  const renderNameColumn = useCallback(
    (profileData: ProfileTableData): JSX.Element => {
      return (
        <NameCellContainer>
          {profileData instanceof Individual && <IndividualAvatar individual={profileData ?? null} />}
          {profileData instanceof Company && <CompanyAvatar company={profileData} />}
          {profileData instanceof Team && <TeamAvatar team={profileData} />}
          <RowTitle>
            {profileData instanceof Individual
              ? `${profileData?.profile?.firstName} ${profileData?.profile?.lastName}`
              : profileData.profile?.name}
          </RowTitle>
        </NameCellContainer>
      );
    },
    [],
  );

  const renderCategoriesColumn = (profileData: ProfileTableData): JSX.Element => {
    if (!profileData.profile?.categories || profileData.profile.categories.length < 1) {
      return <Icon>remove</Icon>;
    }
    return (
      <div>
        {profileData.profile?.categories?.map((label: string) => (
          <CategoryChip key={label} label={label} size="small" />
        ))}
      </div>
    );
  };

  const renderVisibilityColumn = useCallback(
    (profile: ProfileTableData): JSX.Element => {
      return (
        <Tooltip
          title={profile.isVisible ? "Profile is searchable on Marketplace" : "Profile is hidden from Marketplace"}
        >
          <span>
            <Switch
              checked={profile.isVisible}
              disabled={getShouldDisableRowEditing(profile)}
              onClick={(event) => event.stopPropagation()}
              onChange={(event) => {
                event.stopPropagation();
                void publishProfile(profile);
              }}
            />
          </span>
        </Tooltip>
      );
    },
    [getShouldDisableRowEditing, publishProfile],
  );

  const getMenuItems = useCallback(
    (profile: ProfileTableData): React.ReactNode[] => {
      const nodes: React.ReactNode[] = [];
      if (session.context?.viewingAsVendor) {
        nodes.push(
          <MenuItem
            key="publish"
            onClick={(event) => {
              event.stopPropagation();
              void publishProfile(profile);
            }}
          >
            <ListItemIcon>
              {profile.isVisible ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
            </ListItemIcon>
            <ListItemText>{profile.isVisible ? "Hide From Marketplace" : "Show On Marketplace"}</ListItemText>
          </MenuItem>,
        );
      }
      nodes.push([
        <MenuItem key="edit" onClick={() => editProfile(profile)}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit Profile</ListItemText>
        </MenuItem>,
      ]);
      if (activeTab === PageTab.Teams) {
        const teamProfile = profile as Team;
        if (teamProfile.leader?.userId?.isEqualTo(session.user?.id)) {
          nodes.push(
            <MenuItem key="delete" onClick={() => deleteProfile(profile)}>
              <ListItemIcon>
                <DeleteIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Delete</ListItemText>
            </MenuItem>,
          );
        } else {
          nodes.push(
            <MenuItem key="leave" onClick={() => leaveTeam(profile)}>
              <ListItemIcon>
                <ExitIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Leave Team</ListItemText>
            </MenuItem>,
          );
        }
      }
      return nodes;
    },
    [leaveTeam, deleteProfile, editProfile, publishProfile, session, activeTab],
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor(renderNameColumn, {
        header: "Name",
        id: "name",
      }),
      columnHelper.accessor(renderCategoriesColumn, {
        header: "Categories",
        id: "categories",
      }),
      columnHelper.accessor(renderVisibilityColumn, {
        header: "Searchable On Marketplace?",
        id: "isVisible",
      }),
    ],
    [renderNameColumn, renderVisibilityColumn],
  );

  let profiles: ProfileTableData[] = [];
  if (activeTab === PageTab.Company) profiles = companyProfiles ?? [];
  if (activeTab === PageTab.Teams) profiles = teamProfiles ?? [];
  if (activeTab === PageTab.Individual) profiles = individualProfiles ?? [];

  const tableOptions: MRT_TableOptions<ProfileTableData> = {
    columns,
    data: profiles,
    enableRowSelection: false,
    enableTableHead: true,
    enableRowActions: false,
    enableColumnPinning: true,
    initialState: {
      columnPinning: { right: ["mrt-row-actions"] },
    },
    renderToolbarInternalActions: ({ table }) => (
      <>
        {!isMediumDisplaySize && (
          <ToolbarButtonsContainer>
            <ToolbarButtons>
              <ToggleGlobalFilterButton table={table} />
              <ToggleFiltersButton table={table} />
              <ShowHideColumnsButton table={table} />
              <ToggleDensePaddingButton table={table} />
              <ToggleFullScreenButton table={table} />
            </ToolbarButtons>
            <Tooltip title={addProfileDisabledMessage()}>
              <span>
                <NewProfileButtonContainer disabled={getShouldDisableAddButton()}>
                  <Button
                    color="primary"
                    disabled={getShouldDisableAddButton()}
                    startIcon={activeTab ? newProfileIcon(activeTab) : null}
                    onClick={() => navigate(`/marketplace/profiles/${activeTab}/create`)}
                  >
                    {Config.defaultProfileName}
                  </Button>
                </NewProfileButtonContainer>
              </span>
            </Tooltip>
          </ToolbarButtonsContainer>
        )}
        {isMediumDisplaySize && (
          <Tooltip title={addProfileDisabledMessage()}>
            <span>
              <NewProfileButtonContainer disabled={getShouldDisableAddButton()}>
                <NewButton
                  color="primary"
                  disabled={getShouldDisableAddButton()}
                  onClick={() => navigate(`/marketplace/profiles/${activeTab}/create`)}
                >
                  {activeTab ? newProfileIcon(activeTab) : null}
                </NewButton>
              </NewProfileButtonContainer>
            </span>
          </Tooltip>
        )}
      </>
    ),
    renderTopToolbarCustomActions: ({ table }) => {
      if (!activeTab) return null;
      return (
        <TableTopToolbarContainer>
          <TableTitle variant="h4">Marketplace Profiles</TableTitle>
          <LowerTopToolbarContainer>
            <Tabs
              value={activeTab}
              indicatorColor="primary"
              textColor="primary"
              onChange={(_event, newValue) => navigate(`/marketplace/profiles/${newValue}`)}
            >
              <Tab value={PageTab.Individual} label="Individual Profile" />
              <Tab value={PageTab.Company} label="Company Profile" />
              {session.context?.viewingAsVendor && <Tab value={PageTab.Teams} label="Team Profiles" />}
            </Tabs>
          </LowerTopToolbarContainer>
          {isMediumDisplaySize && (
            <ToolbarButtonsContainer>
              <ToolbarButtons>
                <ToggleGlobalFilterButton table={table} />
                <ToggleFiltersButton table={table} />
                <ShowHideColumnsButton table={table} />
                <ToggleDensePaddingButton table={table} />
                <ToggleFullScreenButton table={table} />
              </ToolbarButtons>
            </ToolbarButtonsContainer>
          )}
        </TableTopToolbarContainer>
      );
    },
    renderRowActionMenuItems: ({ row }) => getMenuItems(row.original),
    getRowId: (row) => row.id?.value ?? Guid.generate().value,
    manualFiltering: true,
    enableStickyHeader: true,
    enableStickyFooter: true,
    enableColumnFilters: true,
    manualPagination: true,
    manualSorting: true,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setQuery,
    onPaginationChange: setPagination,
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => editProfile(row.original),
      sx: { cursor: "pointer" },
    }),
    onSortingChange: setSorting,
    muiTableContainerProps: ({ table }) => ({
      sx: {
        height: `calc(100% - ${table.refs.topToolbarRef.current?.offsetHeight}px - ${table.refs.bottomToolbarRef.current?.offsetHeight}px)`,
      },
    }),
    muiTablePaperProps: {
      sx: {
        height: "100%",
      },
    },
    muiToolbarAlertBannerProps:
      (activeTab === PageTab.Individual && errorLoadingIndividualQuery) ||
      (activeTab === PageTab.Company && errorLoadingCompanyQuery) ||
      (activeTab === PageTab.Teams && errorLoadingTeamQuery)
        ? {
            color: "error",
            children: "Error loading data",
          }
        : undefined,
    rowCount:
      (activeTab === PageTab.Individual && individualProfiles?.length) ||
      (activeTab === PageTab.Company && companyProfiles?.length) ||
      (activeTab === PageTab.Teams && teamProfiles?.length) ||
      0,
    state: {
      columnFilters,
      globalFilter: query,
      isLoading:
        (activeTab === PageTab.Individual && individualQueryIsLoading) ||
        (activeTab === PageTab.Company && companyQueryIsLoading) ||
        (activeTab === PageTab.Teams && teamQueryIsLoading),
      pagination,
      showAlertBanner:
        (activeTab === PageTab.Individual && !!errorLoadingIndividualQuery) ||
        (activeTab === PageTab.Company && !!errorLoadingCompanyQuery) ||
        (activeTab === PageTab.Teams && !!errorLoadingTeamQuery),
      showProgressBars:
        (activeTab === PageTab.Individual && individualQueryIsLoading) ||
        (activeTab === PageTab.Company && companyQueryIsLoading) ||
        (activeTab === PageTab.Teams && teamQueryIsLoading),
      sorting,
      showSkeletons:
        (activeTab === PageTab.Individual && individualQueryIsLoading && !individualProfiles) ||
        (activeTab === PageTab.Company && companyQueryIsLoading && !companyProfiles) ||
        (activeTab === PageTab.Teams && teamQueryIsLoading && !teamProfiles),
    },
  };

  if (!sessionPermissions)
    return (
      <LoaderContainer>
        <Loader fontSize="20px" />
      </LoaderContainer>
    );
  if (!sessionPermissions.includesReadWriteAccessToMarketplace) return <NotAuthorized />;

  return <MRTNoMemo<ProfileTableData> options={tableOptions} />;
}
